import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getFocusCycleTarget } from './dialogFocusCycle';
import type { FocusCycleDirection } from './dialogFocusCycle';

let counter = 0;

type UseDialogParams = Readonly<{
  buttonTextCancel?: string;
  buttonTextSuccess?: string;
  id?: string;
  isOpen?: boolean;
}>;

type UseDialogResult = Readonly<{
  closeLabel: string;
  descriptionId: string;
  initialFocusRef: React.RefObject<HTMLDivElement>;
  onPaperKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  resolvedButtonTextCancel: string;
  resolvedButtonTextSuccess: string;
  titleId: string;
}>;

// Cierra el ciclo de Tab dentro del role="dialog" (TAN-11097): sin esto, el
// TrapFocus de MUI v4 hace pasar el foco por .MuiDialog-container (tabindex -1,
// sin indicador) antes de volver al primer control. Ver dialogFocusCycle.ts.
function onPaperKeyDown(event: React.KeyboardEvent<HTMLElement>): void {
  // Alt no se excluye: en Safari (macOS por defecto) Option+Tab es el Tab que
  // recorre botones y enlaces. Ctrl/Cmd+Tab son atajos del navegador.
  if (event.key !== 'Tab' || event.defaultPrevented || event.ctrlKey || event.metaKey) return;
  const paper = event.currentTarget;
  const current = event.target as HTMLElement;
  // Los portales (menús, diálogos anidados) burbujean por el árbol de React:
  // solo actuamos sobre foco que está de verdad en el DOM de este Paper.
  if (!paper.contains(current)) return;

  const direction: FocusCycleDirection = event.shiftKey ? 'backward' : 'forward';
  const target = getFocusCycleTarget(paper, current, direction);
  if (!target) return;

  target.focus();
  // Solo nos quedamos el Tab si el foco se movió de verdad (o ya estaba en su
  // sitio): si focus() falla en silencio, el navegador sigue con su Tab nativo.
  if (target.ownerDocument.activeElement === target) event.preventDefault();
}

function useDialog({ buttonTextCancel, buttonTextSuccess, id, isOpen }: UseDialogParams): UseDialogResult {
  const { t } = useTranslation(['actions']);
  const idRef = useRef<string | null>(null);
  const initialFocusRef = useRef<HTMLDivElement>(null);

  if (idRef.current === null) {
    counter += 1;
    idRef.current = id ?? `dialog-${counter}`;
  }

  // After MUI's TrapFocus settles on Paper, hand focus to a neutral anchor so
  // :focus-visible never highlights an action button on open. Safe for
  // alertdialogs too: Enter on the anchor does not activate Confirm.
  useEffect(() => {
    if (!isOpen) return undefined;
    const rafId = globalThis.requestAnimationFrame(() => {
      initialFocusRef.current?.focus();
    });
    return () => globalThis.cancelAnimationFrame(rafId);
  }, [isOpen]);

  return {
    closeLabel: t('actions:Close'),
    descriptionId: `${idRef.current}-description`,
    initialFocusRef,
    onPaperKeyDown,
    resolvedButtonTextCancel: buttonTextCancel ?? t('actions:Cancel'),
    resolvedButtonTextSuccess: buttonTextSuccess ?? t('actions:Confirm'),
    titleId: `${idRef.current}-title`,
  };
}

export default useDialog;
